import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

interface Props {
  images: string[];
  initialIndex: number;
  onClose: () => void;
}

export default function Lightbox({ images, initialIndex, onClose }: Props) {
  const [index, setIndex] = useState(initialIndex);

  const prev = useCallback(() => setIndex(i => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIndex(i => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose, prev, next]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-primary/95 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-label="Image lightbox"
      aria-modal="true"
    >
      <button onClick={onClose} className="absolute top-6 right-6 text-primary-foreground/70 hover:text-primary-foreground transition-colors z-10" aria-label="Close lightbox">
        <X size={32} aria-hidden="true" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-6 text-primary-foreground/70 hover:text-primary-foreground transition-colors z-10" aria-label="Previous image">
        <ChevronLeft size={40} aria-hidden="true" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-6 text-primary-foreground/70 hover:text-primary-foreground transition-colors z-10" aria-label="Next image">
        <ChevronRight size={40} aria-hidden="true" />
      </button>
      <img
        src={images[index]}
        alt={`Gallery image ${index + 1} of ${images.length}`}
        className="max-w-[90vw] max-h-[85vh] object-contain"
        onClick={e => e.stopPropagation()}
      />
    </div>
  );
}
